import { useLazyQuery } from '@apollo/client';
import {
  AppContext,
  fetchApi,
  getHeaderValue,
  SecurityConfig,
  toast
} from '@nile/components';
import { FormattedMessage } from 'react-intl';
import { AccountClient } from '@nile/graphql';
import { GET_USER_PASSWORD_POLICY } from '@nile/graphql/src/Query/profile';
import envConfig from '@nile/home/src/Config/env';
import * as _ from 'lodash';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import React, { useContext, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';

const UpdateResetPassword = (props) => {
  const { resetPassword, createPassword, isTFARequired } = props;
  const allAttributes = [
    {
      key: 'length',
      helpText: ({ length }) => `Password should be of length ${length}`,
      isValid: false
    },
    {
      key: 'isAlphaNumeric',
      helpText: () => (
        <FormattedMessage id="app.passwordConstraint.alpha.numeric" />
      ),
      isValid: false
    },
    {
      key: 'isMixCaseWithSpecialChar',
      helpText: () => (
        <FormattedMessage id="app.passwordConstraint.mix.special.characters" />
      ),
      isValid: false
    }
  ];

  const [passwordPolicy, setPasswordPolicy] = useState({ length: 6 });

  const [validationAttributes, setValidationAttributes] = useState(
    allAttributes
  );

  const [token, setToken] = useState(null);

  const [renderType, setRenderType] = useState(
    props.renderType || 'currentPassword'
  );
  const [buttonLoader, setButtonLoader] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [verifyToken, setVerifyToken] = useState('');

  const app = useContext(AppContext);

  function filterAttributesToValidate(pwdPolicy) {
    const filteredAttributes = _.clone(allAttributes);
    Object.keys(pwdPolicy).forEach((key) => {
      switch (key) {
        case 'length':
          filteredAttributes[0].helpText = () => {
            return (
              <FormattedMessage
                id="app.passwordConstraint.length"
                values={{ length: pwdPolicy.length }}
              />
            );
          };
          break;
        case 'isAlphaNumeric':
          if (pwdPolicy.isAlphaNumeric === false) {
            filteredAttributes.splice(1, 1);
          }
          break;
        case 'isMixCaseWithSpecialChar':
          if (pwdPolicy.isMixCaseWithSpecialChar === false) {
            filteredAttributes.splice(2, 1);
          }
          break;
        default:
          break;
      }
    });
    return filteredAttributes;
  }

  const [getPasswordPolicyWhenLoggedIn] = useLazyQuery(
    GET_USER_PASSWORD_POLICY,
    {
      client: AccountClient,
      onCompleted: (res) => {
        setPasswordPolicy(res.getCurrentUserPasswordPolicy);
        const policy = res.getCurrentUserPasswordPolicy;
        const attributesToValidate = filterAttributesToValidate(policy);
        setValidationAttributes(attributesToValidate);
      }
    }
  );

  const getRESTPasswordPolicyWhenLoggedIn = async () => {
    const requestOptions = {
      method: 'GET',
      credentials: 'include',
      headers: getHeaderValue()
    };
    const response = await fetchApi(
      `${envConfig.loginRoot}/user/currentuserpwdpolicy`,
      requestOptions
    );
    if (response.status === 200) {
      const pwdPolicy = await response.json();
      setPasswordPolicy(pwdPolicy);
      const policy = pwdPolicy;
      const attributesToValidate = filterAttributesToValidate(policy);
      setValidationAttributes(attributesToValidate);
    }
  };

  const getPasswordPolicyWhenLoggedOut = async (authToken) => {
    const requestOptions = {
      method: 'GET',
      credentials: 'include',
      headers: getHeaderValue()
    };

    const response = await fetchApi(
      `${envConfig.loginRoot}/user/userpwdpolicybytoken?token=${authToken}`,
      requestOptions
    );
    if (response.status === 200) {
      const pwdPolicy = await response.json();
      setPasswordPolicy(pwdPolicy);
      const policy = pwdPolicy;
      const attributesToValidate = filterAttributesToValidate(policy);
      setValidationAttributes(attributesToValidate);
    }
  };

  useEffect(() => {
    const processedToken = queryString.parse(props.location.search).token;

    if (_.includes(props.location.pathname, 'resetpassword')) {
      setToken(props.location.search);
      setToken(processedToken);
      getPasswordPolicyWhenLoggedOut(processedToken);
    }
    if (_.includes(props.location.pathname, 'createnewpassword')) {
      setToken(processedToken);
      getPasswordPolicyWhenLoggedOut(processedToken);
    }

    if (_.includes(props.location.pathname, 'updatepassword')) {
      getPasswordPolicyWhenLoggedIn();
    }

    if (_.includes(props.location.pathname, 'updatenewpassword')) {
      getRESTPasswordPolicyWhenLoggedIn();
    }
  }, [props.location.pathname]);

  const handleUpdatePassword = async (newPassword) => {
    setButtonLoader(true);
    const formData = new FormData();
    formData.append('newPassword', newPassword);
    formData.append('oldPassword', currentPassword);
    formData.append('authenticationVerifiedToken', verifyToken);

    const requestOptions = {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: getHeaderValue()
    };

    const response = await fetchApi(
      `${envConfig.loginRoot}/user/updatepassword`,
      requestOptions
    );

    if (response.status === 200) {
      toast('success', 'app.password.update.success');
      setButtonLoader(false);
      reloadToHome();
    }

    if (response.status !== 200) {
      setButtonLoader(false);
      toast('error', 'Something went wrong');
    }
  };

  const handleResetPassword = async (data) => {
    const formData = new FormData();
    formData.append('newPassword', data);
    formData.append('token', token);
    if (verifyToken)
      formData.append('authenticationVerifiedToken', verifyToken);

    const requestOptions = {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: getHeaderValue()
    };
    setButtonLoader(true);
    const response = await fetchApi(
      `${envConfig.loginRoot}/user/submitresetpassword`,
      requestOptions
    );
    if (response.status === 200) {
      reloadToHome();
      toast('success', `app.password.reset.success`);
    } else {
      toast('error', `app.errorCode.${response.status}`);
    }
  };

  const handleCreateNewPassword = async (newPassword) => {
    const formData = new FormData();
    formData.append('newPassword', newPassword);
    formData.append('token', token);
    const requestOptions = {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: getHeaderValue()
    };

    const response = await fetchApi(
      `${envConfig.loginRoot}/user/submitactivation`,
      requestOptions
    );

    if (response.status === 200) {
      setButtonLoader(false);
      reloadToHome();
    } else {
      setButtonLoader(false);
      toast('error', `app.errorCode.${response.status}`);
    }
  };

  const reloadToHome = () => {
    setTimeout(() => {
      window.location.reload();
    }, 1000);
    props.history.push('/home');
  };

  const verifyCurrentPassword = async (data) => {
    const formData = new FormData();
    formData.append('currentPassword', data);
    const requestOptions = {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: getHeaderValue()
    };
    setButtonLoader(true);
    const response = await fetchApi(
      `${envConfig.loginRoot}/user/verifycurrentpassword`,
      requestOptions
    );
    if (response.status === 200) {
      setCurrentPassword(data);
      setRenderType('passcode');
    } else {
      toast('error', `app.errorCode.${response.status}`);
    }
    setButtonLoader(false);
  };

  const verifyPassword = async (data) => {
    const formData = new FormData();

    formData.append('newPassword', data);
    if (resetPassword || createPassword) formData.append('token', token);

    const requestOptions = {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: getHeaderValue()
    };
    setButtonLoader(true);

    const response = await fetchApi(
      `${envConfig.loginRoot}/user/${
        resetPassword || createPassword
          ? 'validatepasswordbytoken'
          : 'validatepassword'
      }`,
      requestOptions
    );
    let validationObject = await response.json();
    if (isEmpty(validationObject)) {
      if (createPassword) {
        handleCreateNewPassword(data);
      } else if (resetPassword) {
        handleResetPassword(data);
      } else handleUpdatePassword(data);
    } else {
      for (let i in validationObject) {
        toast('error', i);
      }
    }
    setButtonLoader(false);
  };

  const verifyPasswordAndPasscode = async ({ passcode, currentPassword }) => {
    const formData = new FormData();
    formData.append('currentPassword', currentPassword);
    if (passcode) formData.append('passcode', passcode);

    const requestOptions = {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: getHeaderValue()
    };
    setButtonLoader(true);
    const response = await fetchApi(
      `${envConfig.loginRoot}/user/verifycurrentpasswordandpasscode`,
      requestOptions
    );
    if (response.status === 200) {
      let token = await response.text();
      setVerifyToken(token);
      setRenderType('updatePassword');
    } else {
      toast('error', `app.errorCode.${response.status}`);
    }
    setButtonLoader(false);
  };

  const verifyPasswordAndPasscodeByToken = async (data, type) => {
    const token = queryString.parse(props.location.search).token;
    const formData = new FormData();
    formData.append(data.length === 6 ? 'passcode' : 'backupCode', data);
    formData.append('token', token);

    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      headers: getHeaderValue(),
      body: formData
    };
    const response = await fetchApi(
      `${envConfig.loginRoot}/user/verifypasscodeorbackupcodebytoken`,
      requestOptions
    );
    if (response.status === 200) {
      let data = await response.text();
      setVerifyToken(data);
      setRenderType('updatePassword');
    } else {
      toast('error', `app.errorCode.${response.status}`);
    }
    setButtonLoader(false);
  };

  const handleData = async (data, type) => {
    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      headers: getHeaderValue()
    };
    //For reset password coming from remort url
    if (resetPassword) {
      //If TFA is enabled for this user get verification token before reset password
      if (isTFARequired) {
        //After verifying TFA reset password
        setButtonLoader(true);
        if (verifyToken) {
          verifyPassword(data);
        } else verifyPasswordAndPasscodeByToken(data, type);
      }
      //If this user dosent have TFA enabled reset password directly
      else {
        verifyPassword(data);
      }
    } else if (type === 'currentPassword') {
      setButtonLoader(true);
      const response = await fetchApi(
        `${envConfig.loginRoot}/user/tfarequiredforcuruser`,
        requestOptions
      );
      let isTFAEnabled = await response.json();
      if (isTFAEnabled) {
        verifyCurrentPassword(data);
      } else {
        verifyPasswordAndPasscode({ currentPassword: data });
      }
    } else if (type === 'passcode') {
      verifyPasswordAndPasscode({ passcode: data, currentPassword });
    } else if (type === 'updatePassword') {
      verifyPassword(data);
    }
  };
  return (
    <div className="fhp">
      <SecurityConfig
        createPassword={createPassword}
        isPublic
        resetPasswordPublic={resetPassword}
        renderType={renderType}
        handleData={handleData}
        buttonLoader={buttonLoader}
        validatePwdAttributes={validationAttributes}
        passwordPolicy={passwordPolicy}
      />
    </div>
  );
};

export default withRouter(UpdateResetPassword);
